import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPatient } from '@common/interfaces/patient';
import { EpisodeContext } from '@frontend/components/pages/episodes/EpisodeContext';
import { useFormContext } from '@frontend/utils/hooks/useFormContext';
import type { IService } from '../../../../../../common/interfaces/service';
import type { IReduxState } from '../../../../../configureStore';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IProcedureAddEdit } from '../../../procedures/ProcedureItemAddEdit';
import type { IServiceProvider } from '../../../procedures/serviceProvider';
import { never } from '../../../../../../common/utils/never';
import { translateKey } from '../../../../../translate';
import { CareProviderSelection } from '../../../../elements/CareProviderSelection';
import { CheckCareProvider } from './CheckCareProvider';
import { getCareProviderIsActive } from './getCareProviderIsActive';

export interface ICareProviderProps {
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
}

interface ICareProviderSelectorProps {
    automation?: string;
    contextPatient?: IPatient;
    entity: ICareProviderProps;
    errors: ValidationErrors;
    handleChange: <K extends keyof ICareProviderProps>(name: K, value: ICareProviderProps[K]) => Promise<void> | void;
    hideCorrectGpWarning?: boolean;
    isHighPrivacy: boolean;
    procedure?: IProcedureAddEdit;
    requesterIsHighPrivacy?: boolean;
    service?: IService;
    serviceProvider?: IServiceProvider;
    skipValidationOnChange?: boolean;
    validateOne: (name: keyof ICareProviderProps) => Promise<void> | void;
}

enum CareProviderSelectorViews {
    showCheckCareProvider = 'showCheckCareProvider',
    showNothing = 'showNothing',
    showSelector = 'showSelector',
}

export function CareProviderSelector(props: ICareProviderSelectorProps) {
    const episodeContext = useContext(EpisodeContext);
    const patient = episodeContext?.patient || props.contextPatient;
    const currentOrganizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const [view, setView] = useState(CareProviderSelectorViews.showNothing);
    const [isActiveGeneralPractitioner, setIsActiveGeneralPractitioner] = useState<boolean>();
    const form = useFormContext<{ draftCareProviderOrganizationId: string; draftCareProviderPractitionerId: string }>();

    useEffect(() => {
        if (patient) {
            loadIsActiveGeneralPractitioner(patient.careProviderPractitionerId, patient.careProviderOrganizationId);
        }
    }, [patient]);

    useEffect(() => {
        setCareProviderSelectorView();
    }, [isActiveGeneralPractitioner]);

    if (
        props.requesterIsHighPrivacy ||
        (props.isHighPrivacy && (!props.serviceProvider || props.serviceProvider.isServiceConsumerSelector)) ||
        !patient
    ) {
        return null;
    }

    if (view === CareProviderSelectorViews.showCheckCareProvider) {
        return (
            <CheckCareProvider
                hideCorrectGpWarning={props.hideCorrectGpWarning}
                isRequired={!props.procedure?.careProviderIsNotRequester}
                onClickChange={() => {
                    setView(CareProviderSelectorViews.showSelector);
                }}
                patient={patient}
            />
        );
    } else if (view === CareProviderSelectorViews.showSelector) {
        return (
            <CareProviderSelection
                automation="choosePractitioner"
                error={
                    props.errors.get('draftCareProviderOrganizationId') ??
                    props.errors.get('draftCareProviderPractitionerId')
                }
                formlabeltext={translateKey('patient.careProviderPractitioner')}
                hasErrors={
                    props.errors.has('draftCareProviderOrganizationId') ??
                    props.errors.has('draftCareProviderPractitionerId')
                }
                initialOrganizationId={props.entity ? props.entity.draftCareProviderOrganizationId : null}
                initialPractitionerId={props.entity ? props.entity.draftCareProviderPractitionerId : null}
                isActiveGeneralPractitioner={isActiveGeneralPractitioner}
                onChange={async (organizationId, practitionerId) => {
                    await props.handleChange('draftCareProviderOrganizationId', organizationId);
                    await props.handleChange('draftCareProviderPractitionerId', practitionerId);

                    form?.handleChange([
                        { name: 'draftCareProviderOrganizationId', value: organizationId },
                        { name: 'draftCareProviderPractitionerId', value: practitionerId },
                    ]);
                }}
                onDoValidate={async () => {
                    if (!props.skipValidationOnChange) {
                        await props.validateOne('draftCareProviderOrganizationId');
                        await props.validateOne('draftCareProviderPractitionerId');
                    }
                }}
                patient={patient}
                required={!props.procedure?.careProviderIsNotRequester}
                size="medium"
            />
        );
    } else if (view === CareProviderSelectorViews.showNothing) {
        return null;
    } else {
        never(view);
    }

    async function setCareProviderSelectorView() {
        if (!patient) {
            setView(CareProviderSelectorViews.showNothing);

            return;
        }

        if (
            !isActiveGeneralPractitioner ||
            props.entity.draftCareProviderPractitionerId !== patient.careProviderPractitionerId
        ) {
            setView(CareProviderSelectorViews.showSelector);
        } else if (patient.careProviderOrganizationId !== currentOrganizationId) {
            setView(CareProviderSelectorViews.showCheckCareProvider);
        } else if (isActiveGeneralPractitioner && patient.careProviderOrganizationId === currentOrganizationId) {
            setView(CareProviderSelectorViews.showSelector);
        }
    }

    async function loadIsActiveGeneralPractitioner(
        careProviderPractitionerId?: string | null,
        careProviderOrganizationId?: string | null
    ): Promise<void> {
        const activeCareProvider = await getCareProviderIsActive(
            careProviderPractitionerId,
            careProviderOrganizationId,
            props.service?.id
        );
        setIsActiveGeneralPractitioner(activeCareProvider);
    }
}
